import { parseImageMeta } from "@/lib/imageMeta";

/**
 * PositionedImage — renders an <img> with focal-point position + zoom read
 * from the URL fragment (see /lib/imageMeta.js).
 *
 * Drop-in replacement for <img> in any place that needs to honour the
 * admin-configured framing. When zoom > 1 we wrap in a span with
 * overflow:hidden so the scaled image is clipped to the original frame.
 *
 * All existing classes (e.g. "w-16 h-16 rounded-xl object-cover") can be
 * passed through `className` and will be applied to the visible box.
 */
export default function PositionedImage({ src, alt = "", className = "", style = {}, testId, draggable, loading, ...rest }) {
  const { baseUrl, position, zoom } = parseImageMeta(src);
  const zoomed = zoom > 1.001;

  // When no zoom, render a plain <img> so layout/flex behaviour is unchanged
  if (!zoomed) {
    return (
      <img
        src={baseUrl}
        alt={alt}
        className={className}
        style={{ objectPosition: position, ...style }}
        data-testid={testId}
        draggable={draggable}
        loading={loading}
        {...rest}
      />
    );
  }

  // When zoomed, wrap in an overflow-hidden span so transform:scale is clipped
  // to the original frame. Strip "object-cover" from outer class and apply it
  // to the inner img instead (idempotent if already present).
  return (
    <span
      className={`relative inline-block overflow-hidden ${className}`}
      style={style}
      data-testid={testId}
    >
      <img
        src={baseUrl}
        alt={alt}
        className="block w-full h-full object-cover"
        style={{
          objectPosition: position,
          transform: `scale(${zoom})`,
          transformOrigin: position,
        }}
        draggable={draggable}
        loading={loading}
      />
    </span>
  );
}
